import {
  CmpInvoiceAccountingExportFieldsFragment,
  CmpInvoiceAccountingExportProfileEnum,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceOverdueBucketEnum,
  CmpInvoiceSourceFilterEnum,
} from "../../../../graphql/types";
import {
  getTimeZoneCalendarDateEndUtcMilli,
  getTimeZoneCalendarDateStartUtcMilli,
} from "../../../common/dashboard/formatters";
import {
  getPreserveExpectedErrorsForCallerContext,
} from "../../../../utils/apolloErrorPolicy";
import { dateOrderMessage } from "../../../../utils/fieldMessages";
import {
  buildStaffErrorFallback,
  UserFacingError,
} from "../../../../utils/userFacingError";

export const SAND_BAY_ACCOUNTING_SOFTWARE_EXPORT_CHAMBER_ID =
  "2fa7868a-7ec6-4e4d-8b32-2da14fc51be6";

export const accountingSoftwareExportTriggerLabel =
  "Export for accounting software";

export const accountingExportApolloContext =
  getPreserveExpectedErrorsForCallerContext({
    expectedGraphQLErrorCodes: [
      "CMP_ACCOUNTING_EXPORT_DATA_REPAIR_REQUIRED",
      "CMP_ACCOUNTING_EXPORT_DATE_RANGE_INVALID",
      "CMP_ACCOUNTING_EXPORT_DATE_RANGE_REQUIRED",
      "CMP_ACCOUNTING_EXPORT_NOT_AVAILABLE",
    ],
  });

export type ManageInvoiceExportRequest = {
  displayStates?: CmpInvoiceDisplayStateEnum[];
  invoiceDateFromUtcMilli?: number;
  invoiceDateToUtcMilli?: number;
  label: string;
  overdueBuckets?: CmpInvoiceOverdueBucketEnum[];
  profile: CmpInvoiceAccountingExportProfileEnum;
  search?: string;
  sourceFilter?: CmpInvoiceSourceFilterEnum;
  useCurrentFilters: boolean;
};

export type ManageInvoiceExportAction = {
  label: string;
  request: ManageInvoiceExportRequest;
};

export const genericInvoiceCsvExportActions: readonly ManageInvoiceExportAction[] = [
  {
    label: "Export All Finalized Invoices (Generic CSV)",
    request: {
      label: "All finalized invoices",
      profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
      useCurrentFilters: false,
    },
  },
  {
    label: "Export Outstanding Invoices (Generic CSV)",
    request: {
      displayStates: [CmpInvoiceDisplayStateEnum.NotPaid],
      label: "Outstanding invoices",
      profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
      useCurrentFilters: false,
    },
  },
  {
    label: "Export Overdue Invoices (Generic CSV)",
    request: {
      label: "Overdue invoices",
      overdueBuckets: [
        CmpInvoiceOverdueBucketEnum.OverdueUnder_60,
        CmpInvoiceOverdueBucketEnum.Overdue_60_89,
        CmpInvoiceOverdueBucketEnum.Overdue_90_119,
        CmpInvoiceOverdueBucketEnum.Overdue_120Plus,
      ],
      profile: CmpInvoiceAccountingExportProfileEnum.GenericCsv,
      useCurrentFilters: false,
    },
  },
];

export const accountingSoftwareExportActions: readonly ManageInvoiceExportAction[] = [
  {
    label: "Export QuickBooks general CSV",
    request: {
      label: "Selected invoice date range for QuickBooks Online",
      profile: CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline,
      // The dialog adds its required invoice-date range when this action runs.
      // Other independent Manage Invoices filters remain intentionally separate.
      useCurrentFilters: false,
    },
  },
  {
    label: "Export Sage general CSV",
    request: {
      label: "Selected invoice date range for Sage Accounting",
      profile: CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud,
      useCurrentFilters: false,
    },
  },
];

export const shouldShowAccountingSoftwareExport = (chamberId: string) =>
  chamberId === SAND_BAY_ACCOUNTING_SOFTWARE_EXPORT_CHAMBER_ID;

type AccountingSoftwareExportDateRangeInput = {
  invoiceDateFromUtcMilli: number;
  invoiceDateToUtcMilli: number;
};

/** The date field labels on the screen that starts the export. */
type AccountingExportDateLabels = { end: string; start: string };

// Manage Invoices > Export for accounting software dialog labels.
const MANAGE_INVOICES_EXPORT_DATE_LABELS: AccountingExportDateLabels = {
  end: "End date",
  start: "Start date",
};

export const buildAccountingSoftwareExportDateRangeInput = ({
  dateLabels = MANAGE_INVOICES_EXPORT_DATE_LABELS,
  endDate,
  startDate,
  timeZone,
}: {
  /** Field labels for the messages. Reports passes its own labels. */
  dateLabels?: AccountingExportDateLabels;
  endDate: string;
  startDate: string;
  timeZone: string;
}): AccountingSoftwareExportDateRangeInput => {
  if (!startDate || !endDate) {
    throw new UserFacingError(
      `To download for Sage or QuickBooks, select the ${dateLabels.start} and the ${dateLabels.end}.`
    );
  }

  const isValidCalendarDate = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

    if (!match) {
      return false;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const utcDate = new Date(Date.UTC(year, month - 1, day));

    return (
      utcDate.getUTCFullYear() === year &&
      utcDate.getUTCMonth() === month - 1 &&
      utcDate.getUTCDate() === day
    );
  };

  if (!isValidCalendarDate(startDate) || !isValidCalendarDate(endDate)) {
    throw new UserFacingError(
      `Select the ${dateLabels.start} and the ${dateLabels.end} from the calendar.`
    );
  }

  const invoiceDateFromUtcMilli =
    getTimeZoneCalendarDateStartUtcMilli(startDate, timeZone);
  const invoiceDateToUtcMilli = getTimeZoneCalendarDateEndUtcMilli(
    endDate,
    timeZone
  );

  if (invoiceDateFromUtcMilli > invoiceDateToUtcMilli) {
    throw new UserFacingError(
      dateOrderMessage({
        earlierLabel: dateLabels.start,
        laterLabel: dateLabels.end,
      })
    );
  }

  return {
    invoiceDateFromUtcMilli,
    invoiceDateToUtcMilli,
  };
};

export const getInvoiceExportProfileLabel = (
  profile: CmpInvoiceAccountingExportProfileEnum
) => {
  switch (profile) {
    case CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline:
      return "QuickBooks general CSV";
    case CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud:
      return "Sage general CSV";
    case CmpInvoiceAccountingExportProfileEnum.GenericCsv:
    default:
      return "Generic CSV";
  }
};

export const getAccountingSoftwareExportButtonLabel = (
  profile: CmpInvoiceAccountingExportProfileEnum
) => {
  switch (profile) {
    case CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud:
      return "Sage CSV";
    case CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline:
    default:
      return "QuickBooks CSV";
  }
};

export const getInvoiceExportReviewGuidance = (
  profile: CmpInvoiceAccountingExportProfileEnum
) => {
  switch (profile) {
    case CmpInvoiceAccountingExportProfileEnum.SageBusinessCloud:
      return "This is a plain list of invoices in a CSV file. You may need to match its columns to Sage before you import it. It is not a Sage import file or a backup.";
    case CmpInvoiceAccountingExportProfileEnum.QuickbooksOnline:
      return "This is a plain list of invoices in a CSV file. You may need to match its columns to QuickBooks before you import it. It is not a QuickBooks import file or a backup.";
    case CmpInvoiceAccountingExportProfileEnum.GenericCsv:
    default:
      return "Review the CSV before importing it into another system.";
  }
};

type InvoiceAccountingExportFile = Pick<
  CmpInvoiceAccountingExportFieldsFragment,
  | "contentType"
  | "csv"
  | "filename"
  | "payerDataWarning"
  | "profile"
  | "rowCount"
>;

type InvoiceAccountingExportPayload = {
  actionCompletedSuccessfully: boolean;
  export?: InvoiceAccountingExportFile | null;
};

type DownloadTextFile = (input: {
  content: string;
  contentType: string;
  filename: string;
}) => void;

export const downloadCompletedInvoiceExport = ({
  download,
  payload,
}: {
  download: DownloadTextFile;
  payload?: InvoiceAccountingExportPayload | null;
}) => {
  if (!payload?.actionCompletedSuccessfully) {
    // Staff-ready text: the server checks refused this export.
    throw new UserFacingError(
      buildStaffErrorFallback("export these invoices", "No file was downloaded.")
    );
  }

  if (!payload.export) {
    throw new Error(
      "Invoice export completed without a CSV file. No file was downloaded."
    );
  }

  download({
    content: payload.export.csv,
    contentType: payload.export.contentType,
    filename: payload.export.filename,
  });

  return payload.export;
};
